import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { RootState } from "@store/index";
import { trpc } from "@utils/api";
import { type inferRouterOutputs, type inferRouterInputs } from "@trpc/server";
import { type AppRouter } from "@server/api/root";

type RouterOutput = inferRouterOutputs<AppRouter>;
type RouterInput = inferRouterInputs<AppRouter>;

export type ListAgent = RouterOutput["agents"]["getAgents"][number];
export type GetAgent = RouterOutput["agents"]["getAgent"];
export type AvailableTools = RouterOutput["agents"]["getToolMarketplace"];
type CreateAgentInput = RouterInput["agents"]["createAgent"];
type UpdateAgentInput = RouterInput["agents"]["updateAgent"];

export type ToolItem = {
  id: string;
  name: string;
  displayName: string;
  description: string;
  kind: string;
};

export type SelectedTool = ToolItem | null;

export type ToolBindingRow = {
  id: string;
  toolName: string;
  toolDisplayName: string;
  toolKind: string;
  toolDescription: string | null;
  nameOverride: string | null;
  descriptionOverride: string | null;
};

/** Persona configuration for agent voice and formatting */
export type PersonaConfig = {
  /** Display name for the persona (e.g., "Brett") */
  name: string;
  /** Voice configuration with style and format rules */
  voice: {
    /** Style guidelines as a single string (voice, tone, personality) */
    style: string;
    /** Formatting constraints */
    format?: {
      /** Maximum character count for messages */
      maxChars?: number;
      /** Whether to avoid emojis */
      noEmojis?: boolean;
      /** Whether to avoid hyphens */
      noHyphens?: boolean;
      /** Whether to avoid bullet points */
      noBulletPoints?: boolean;
      /** Maximum questions per message */
      maxQuestionsPerMessage?: number;
      /** Whether to avoid sign-offs like "Cheers" or "Thanks" */
      noSignOffs?: boolean;
    };
  };
};

/** Respond block configuration for explicit message output */
export type RespondConfig = {
  /** Optional Liquid condition - if false, no message is sent */
  if?: string;
  /** Direct message content (no persona transformation) - mutually exclusive with instructions */
  content?: string;
  /** Instructions for persona to write message (Liquid template) - mutually exclusive with content */
  instructions?: string;
  /** If true, message requires human approval before sending */
  requiresApproval?: boolean;
};

export type AgentFormState = {
  id: string;
  name: string;
  handle: string;
  description: string;
  system: string;
  llmModelId: string;
  /** Structured persona configuration for voice and formatting */
  persona?: PersonaConfig | null;
  /** Stages for multi-stage agents */
  stages: StageFormState[];
  /** Input schema for multi-stage agents */
  inputs: Record<
    string,
    { type: string; required?: boolean; description?: string }
  >;
  /** Error handling strategy */
  onError: "fail" | "skip" | "retry";
  /** Respond block for explicit message output */
  respond?: RespondConfig | null;
};

/** Inline agent definition for agent stages */
export type StageAgentConfig = {
  handle: string;
  name: string;
  description?: string;
  system: string;
  personaName?: string;
  persona?: string;
  llmModelId?: string;
  tools?: Array<
    | string
    | {
        tool: string;
        handle?: string;
        name?: string;
        description?: string;
        requiresApproval?: boolean;
      }
  >;
  outputSchema?: Record<string, unknown>;
};

export type StageFormState = {
  id: string;
  type: "tool" | "agent" | "transform" | "output";
  needs: string[];
  if?: string;
  resource?: string;
  prompt?: string;
  inputs?: Record<string, string>;
  outputs?: Record<string, string>;
  /** Inline agent definition (used by agent stages) */
  props?: StageAgentConfig;
};

export interface AgentState {
  agents: {
    isLoading: boolean;
    data: ListAgent[];
  };
  currentAgent: {
    isLoading: boolean;
    data: GetAgent | null;
  };
  editor: {
    isSaving: boolean;
    isDeleting: boolean;
    isOptimizing: boolean;
    isPublishing: boolean;
    /** The original saved data from the server */
    original: AgentFormState | null;
    /** The working copy that can be modified as the user edits */
    working: AgentFormState | null;
    /** Currently selected version ID */
    selectedVersionId: string | null;
    /** Whether the selected version is published */
    selectedVersionIsPublished: boolean;
    /** All versions for the current agent */
    versions: Array<{
      id: string;
      version: number | null;
      versionLabel: string | null;
      isPublished: boolean;
      trafficWeight: number;
      createdAt: string;
    }>;
    /** Whether versions are loading */
    versionsLoading: boolean;
  };
  /** ID of agent currently being optimized from the list view */
  optimizingAgentId: string | null;
  /** Last optimization stats */
  lastOptimizationStats: {
    directToolCount: number;
    subAgentCount: number;
    totalSubAgentTools: number;
  } | null;
  /** Flow canvas state */
  flow: {
    /** Currently scoped agent for sidebar editing */
    scopedAgentId: string | null;
    /** Currently scoped tool for sidebar editing */
    scopedToolId: string | null;
    /** Agent positions on the canvas (agentId -> {x, y}) */
    positions: Record<string, { x: number; y: number }>;
    /** New tool form state for creating agent connections */
    newToolForm: {
      kind: "SYSTEM" | "AGENT" | "MCP" | null;
      targetAgentId: string | null;
      targetAgentName: string | null;
      targetAgentHandle: string | null;
      modelHandle: string | null;
      operation: string | null;
      nameOverride: string | null;
      descriptionOverride: string | null;
    } | null;
    /** Edit tool binding form state for existing bindings */
    editToolForm: {
      bindingId: string;
      toolName: string;
      toolDisplayName: string;
      toolDescription: string;
      toolKind: string;
      nameOverride: string | null;
      descriptionOverride: string | null;
    } | null;
  };
  /** Tool marketplace state */
  marketplace: {
    isLoading: boolean;
    availableTools: AvailableTools | null;
    searchQuery: string;
    selectedTool: SelectedTool;
    activeTab: "crm" | "integrations";
  };
  /** Draft versioning state */
  draft: {
    /** Whether draft mode is active */
    isActive: boolean;
    /** Deployment group ID linking all draft versions */
    deploymentGroupId: string | null;
    /** The working config being edited (AgentSchema) */
    workingConfig: Record<string, unknown> | null;
    /** The last saved config (for dirty detection) */
    lastSavedConfig: Record<string, unknown> | null;
    /** Whether a save is in progress */
    isSaving: boolean;
    /** Whether a commit is in progress */
    isCommitting: boolean;
    /** Whether there are unsaved local changes */
    isDirty: boolean;
  };
}

const initialState: AgentState = {
  agents: {
    isLoading: false,
    data: [],
  },
  currentAgent: {
    isLoading: false,
    data: null,
  },
  editor: {
    isSaving: false,
    isDeleting: false,
    isOptimizing: false,
    isPublishing: false,
    original: null,
    working: null,
    selectedVersionId: null,
    selectedVersionIsPublished: true,
    versions: [],
    versionsLoading: false,
  },
  optimizingAgentId: null,
  lastOptimizationStats: null,
  flow: {
    scopedAgentId: null,
    scopedToolId: null,
    positions: {},
    newToolForm: null,
    editToolForm: null,
  },
  marketplace: {
    isLoading: false,
    availableTools: null,
    searchQuery: "",
    selectedTool: null,
    activeTab: "crm",
  },
  draft: {
    isActive: false,
    deploymentGroupId: null,
    workingConfig: null,
    lastSavedConfig: null,
    isSaving: false,
    isCommitting: false,
    isDirty: false,
  },
};

// Async Thunks
export const getAgents = createAsyncThunk("agents/getAgents", async () => {
  return await trpc.agents.getAgents.query();
});

export const getAvailableTools = createAsyncThunk(
  "agents/getAvailableTools",
  async () => {
    return await trpc.agents.getToolMarketplace.query();
  },
);

/**
 * Persist agent positions to the database (debounced in UI)
 */
export const saveAgentPositions = createAsyncThunk(
  "agents/saveAgentPositions",
  async (positions: Array<{ agentId: string; x: number; y: number }>) => {
    return await trpc.agents.updateAgentPositions.mutate(positions);
  },
);

export const getAgent = createAsyncThunk(
  "agents/getAgent",
  async (input: { id: string }) => {
    return await trpc.agents.getAgent.query(input);
  },
);

export const createAgent = createAsyncThunk(
  "agents/createAgent",
  async (input: CreateAgentInput) => {
    return await trpc.agents.createAgent.mutate(input);
  },
);

export const updateAgent = createAsyncThunk<
  GetAgent,
  void,
  { state: RootState }
>("agents/updateAgent", async (_, { getState }) => {
  const state = getState();
  const working = state.agents.editor.working;

  if (!working) {
    throw new Error("Cannot update agent without working state");
  }

  const payload: UpdateAgentInput = {
    id: working.id,
    name: working.name,
    description: working.description,
    system: working.system,
    llmModelId: working.llmModelId,
    persona: working.persona,
  };

  await trpc.agents.updateAgent.mutate(payload);

  // Re-fetch the agent to get updated data
  return await trpc.agents.getAgent.query({ id: working.id });
});

export const deleteAgent = createAsyncThunk(
  "agents/deleteAgent",
  async (input: { id: string }) => {
    await trpc.agents.deleteAgent.mutate(input);
    return input.id;
  },
);

export const optimizeAgent = createAsyncThunk<
  { description: string; system: string },
  void,
  { state: RootState }
>("agents/optimizeAgent", async (_, { getState }) => {
  const state = getState();
  const working = state.agents.editor.working;
  const currentAgent = state.agents.currentAgent.data;

  if (!working || !currentAgent) {
    throw new Error("Cannot optimize agent without working state");
  }

  return await trpc.agents.optimizeAgent.mutate({
    id: working.id,
    currentName: working.name,
    currentDescription: working.description,
    currentSystem: working.system,
  });
});

/**
 * Deep optimize agent - analyzes all tools, sub-agent tools, and sub-agent prompts
 * to generate a comprehensive, context-aware system prompt.
 */
export const deepOptimizeAgent = createAsyncThunk<
  {
    description: string;
    system: string;
    stats: {
      directToolCount: number;
      subAgentCount: number;
      totalSubAgentTools: number;
    };
  },
  void,
  { state: RootState }
>("agents/deepOptimizeAgent", async (_, { getState }) => {
  const state = getState();
  const working = state.agents.editor.working;
  const currentAgent = state.agents.currentAgent.data;

  if (!working || !currentAgent) {
    throw new Error("Cannot optimize agent without working state");
  }

  return await trpc.agents.deepOptimizeAgent.mutate({
    id: working.id,
    currentName: working.name,
    currentDescription: working.description,
    currentSystem: working.system,
  });
});

/**
 * Deep optimize a specific agent by ID (for use from the list page).
 * Fetches agent data, optimizes it, and saves the result.
 */
export const deepOptimizeAgentById = createAsyncThunk<
  {
    agent: GetAgent;
    stats: {
      directToolCount: number;
      subAgentCount: number;
      totalSubAgentTools: number;
    };
  },
  { id: string }
>("agents/deepOptimizeAgentById", async (input) => {
  // First fetch the agent to get current values
  const agent = await trpc.agents.getAgent.query({ id: input.id });

  // Run deep optimization
  const optimized = await trpc.agents.deepOptimizeAgent.mutate({
    id: agent.id,
    currentName: agent.name,
    currentDescription: agent.description,
    currentSystem: agent.system,
  });

  // Save the optimized values
  await trpc.agents.updateAgent.mutate({
    id: agent.id,
    name: agent.name,
    description: optimized.description,
    system: optimized.system,
    llmModelId: agent.llmModelId,
  });

  // Fetch updated agent
  const updatedAgent = await trpc.agents.getAgent.query({ id: input.id });

  return {
    agent: updatedAgent,
    stats: optimized.stats,
  };
});

/**
 * Save draft - creates or updates a draft version in the database.
 */
export const saveDraft = createAsyncThunk<
  {
    deploymentGroupId: string;
    versionId: string;
    version: number;
  },
  { config: Record<string, unknown>; deploymentGroupId?: string },
  { state: RootState }
>("agents/saveDraft", async (input) => {
  const result = await trpc.agents.saveDraft.mutate({
    config: input.config,
    deploymentGroupId: input.deploymentGroupId,
  });

  return {
    deploymentGroupId: result.version.deploymentGroupId!,
    versionId: result.version.id,
    version: result.version.version!,
  };
});

/**
 * Commit draft - publish all versions in a deployment group.
 */
export const commitDraft = createAsyncThunk<
  { publishedCount: number },
  { deploymentGroupId: string }
>("agents/commitDraft", async (input) => {
  const result = await trpc.agents.commitDraft.mutate({
    deploymentGroupId: input.deploymentGroupId,
  });

  return {
    publishedCount: result.publishedCount,
  };
});

/**
 * Discard draft - delete all unpublished versions in a deployment group.
 */
export const discardDraft = createAsyncThunk<
  { deletedCount: number },
  { deploymentGroupId: string }
>("agents/discardDraft", async (input) => {
  const result = await trpc.agents.discardDraft.mutate({
    deploymentGroupId: input.deploymentGroupId,
  });

  return {
    deletedCount: result.deletedCount,
  };
});

/**
 * Load all versions for an agent.
 */
export const loadAgentVersions = createAsyncThunk(
  "agents/loadAgentVersions",
  async (input: { agentId: string }) => {
    return await trpc.agents.listVersions.query(input);
  },
);

/**
 * Load a specific version's config into the editor.
 */
export const loadAgentVersion = createAsyncThunk(
  "agents/loadAgentVersion",
  async (input: { agentId: string; versionId: string }) => {
    return await trpc.agents.getAgentVersion.query(input);
  },
);

/**
 * Update an existing draft version in place.
 */
export const updateDraft = createAsyncThunk<
  {
    versionId: string;
    version: number | null;
    versionLabel: string | null;
    isPublished: boolean;
  },
  { versionId: string; config: Record<string, unknown> }
>("agents/updateDraft", async (input) => {
  const result = await trpc.agents.updateDraft.mutate(input);
  return {
    versionId: result.version.id,
    version: result.version.version,
    versionLabel: result.version.versionLabel,
    isPublished: result.version.isPublished,
  };
});

/**
 * Publish a draft version.
 */
export const publishVersion = createAsyncThunk<
  {
    versionId: string;
    version: number | null;
    versionLabel: string | null;
    isPublished: boolean;
    trafficWeight: number;
  },
  { versionId: string }
>("agents/publishVersion", async (input) => {
  const result = await trpc.agents.publishVersion.mutate(input);
  return {
    versionId: result.version.id,
    version: result.version.version,
    versionLabel: result.version.versionLabel,
    isPublished: result.version.isPublished,
    trafficWeight: result.version.trafficWeight,
  };
});

/**
 * Delete a draft version.
 */
export const deleteDraftVersion = createAsyncThunk<
  { versionId: string },
  { versionId: string }
>("agents/deleteDraftVersion", async (input) => {
  await trpc.agents.deleteDraft.mutate(input);
  return { versionId: input.versionId };
});

// Slice
export const agentSlice = createSlice({
  name: "agents",
  initialState,
  reducers: {
    updateEditorField(
      state,
      action: PayloadAction<{
        key: keyof AgentFormState;
        value: unknown;
      }>,
    ) {
      if (!state.editor.working) {
        return;
      }
      const { key, value } = action.payload;
      (state.editor.working as Record<string, unknown>)[key] = value;
    },
    /** Add a new stage to the working state */
    addStage(state, action: PayloadAction<StageFormState>) {
      if (!state.editor.working) return;
      state.editor.working.stages.push(action.payload);
    },
    /** Update a stage in the working state */
    updateStage(
      state,
      action: PayloadAction<{ index: number; stage: StageFormState }>,
    ) {
      if (!state.editor.working) return;
      const { index, stage } = action.payload;
      if (index >= 0 && index < state.editor.working.stages.length) {
        state.editor.working.stages[index] = stage;
      }
    },
    /** Remove a stage from the working state */
    removeStage(state, action: PayloadAction<number>) {
      if (!state.editor.working) return;
      const index = action.payload;
      if (index >= 0 && index < state.editor.working.stages.length) {
        const removedStageId = state.editor.working.stages[index]?.id;
        state.editor.working.stages.splice(index, 1);
        // Update needs arrays to remove references to the deleted stage
        if (removedStageId) {
          for (const stage of state.editor.working.stages) {
            stage.needs = stage.needs.filter((id) => id !== removedStageId);
          }
        }
      }
    },
    /** Reorder stages (move from one index to another) */
    reorderStages(
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>,
    ) {
      if (!state.editor.working) return;
      const { fromIndex, toIndex } = action.payload;
      const stages = state.editor.working.stages;
      if (
        fromIndex >= 0 &&
        fromIndex < stages.length &&
        toIndex >= 0 &&
        toIndex < stages.length
      ) {
        const [removed] = stages.splice(fromIndex, 1);
        if (removed) {
          stages.splice(toIndex, 0, removed);
          // Update needs based on new order - each stage depends on the previous one
          for (let i = 0; i < stages.length; i++) {
            const stage = stages[i];
            if (stage && i > 0) {
              const prevStage = stages[i - 1];
              if (prevStage && !stage.needs.includes(prevStage.id)) {
                // Only auto-add dependency if stage has no explicit needs
                if (stage.needs.length === 0) {
                  stage.needs = [prevStage.id];
                }
              }
            }
          }
        }
      }
    },
    /** Update agent inputs schema */
    updateAgentInputs(
      state,
      action: PayloadAction<
        Record<
          string,
          { type: string; required?: boolean; description?: string }
        >
      >,
    ) {
      if (!state.editor.working) return;
      state.editor.working.inputs = action.payload;
    },
    resetEditor(state) {
      state.editor = {
        isSaving: false,
        isDeleting: false,
        isOptimizing: false,
        isPublishing: false,
        original: null,
        working: null,
        selectedVersionId: null,
        selectedVersionIsPublished: true,
        versions: [],
        versionsLoading: false,
      };
    },
    /** Set the selected version ID */
    setSelectedVersionId(state, action: PayloadAction<string | null>) {
      state.editor.selectedVersionId = action.payload;
    },
    /** Initialize editor from a ListAgent (for sidebar editing) */
    initEditorFromAgent(
      state,
      action: PayloadAction<{
        id: string;
        name: string;
        handle?: string;
        description: string;
        system: string;
        llmModelId: string;
        persona?: PersonaConfig | null;
        stages?: StageFormState[];
        inputs?: Record<
          string,
          { type: string; required?: boolean; description?: string }
        >;
        onError?: "fail" | "skip" | "retry";
      }>,
    ) {
      const formState: AgentFormState = {
        id: action.payload.id,
        name: action.payload.name,
        handle: action.payload.handle ?? "",
        description: action.payload.description,
        system: action.payload.system,
        llmModelId: action.payload.llmModelId,
        persona: action.payload.persona,
        stages: action.payload.stages ?? [],
        inputs: action.payload.inputs ?? {},
        onError: action.payload.onError ?? "fail",
      };
      state.editor.original = formState;
      state.editor.working = {
        ...formState,
        stages: [...(action.payload.stages ?? [])],
      };
    },
    resetCurrentAgent(state) {
      state.currentAgent = {
        isLoading: false,
        data: null,
      };
      state.editor = {
        isSaving: false,
        isDeleting: false,
        isOptimizing: false,
        isPublishing: false,
        original: null,
        working: null,
        selectedVersionId: null,
        selectedVersionIsPublished: true,
        versions: [],
        versionsLoading: false,
      };
    },
    /** Reset working copy to match the original saved data */
    resetWorkingToOriginal(state) {
      if (state.editor.original) {
        state.editor.working = { ...state.editor.original };
      }
    },
    /** Flow canvas: scope an agent for sidebar editing */
    scopeFlowAgent(state, action: PayloadAction<string | null>) {
      state.flow.scopedAgentId = action.payload;
      state.flow.scopedToolId = null;
    },
    /** Flow canvas: scope a tool for sidebar editing */
    scopeFlowTool(
      state,
      action: PayloadAction<{ agentId: string; toolId: string } | null>,
    ) {
      if (action.payload) {
        state.flow.scopedAgentId = action.payload.agentId;
        state.flow.scopedToolId = action.payload.toolId;
      } else {
        state.flow.scopedToolId = null;
      }
    },
    /** Flow canvas: update agent position */
    updateAgentPosition(
      state,
      action: PayloadAction<{ agentId: string; x: number; y: number }>,
    ) {
      const { agentId, x, y } = action.payload;
      state.flow.positions[agentId] = { x, y };
    },
    /** Flow canvas: update multiple agent positions (batch) */
    updateAgentPositions(
      state,
      action: PayloadAction<Array<{ agentId: string; x: number; y: number }>>,
    ) {
      for (const { agentId, x, y } of action.payload) {
        state.flow.positions[agentId] = { x, y };
      }
    },
    /** Flow canvas: clear scoped state */
    clearFlowScope(state) {
      state.flow.scopedAgentId = null;
      state.flow.scopedToolId = null;
      state.flow.newToolForm = null;
    },
    /** Flow canvas: initialize new tool form for agent connection */
    initNewAgentToolForm(
      state,
      action: PayloadAction<{
        targetAgentId: string;
        targetAgentName: string;
        targetAgentHandle: string;
      }>,
    ) {
      const defaultName = `Route to ${action.payload.targetAgentName}`;
      const defaultDescription = `Hand off conversation to ${action.payload.targetAgentName}`;
      state.flow.newToolForm = {
        kind: "AGENT",
        targetAgentId: action.payload.targetAgentId,
        targetAgentName: action.payload.targetAgentName,
        targetAgentHandle: action.payload.targetAgentHandle,
        modelHandle: null,
        operation: null,
        nameOverride: defaultName,
        descriptionOverride: defaultDescription,
      };
    },
    /** Flow canvas: update new tool form field */
    updateNewToolForm(
      state,
      action: PayloadAction<{
        kind?: "SYSTEM" | "AGENT" | "MCP" | null;
        targetAgentId?: string | null;
        targetAgentName?: string | null;
        targetAgentHandle?: string | null;
        modelHandle?: string | null;
        operation?: string | null;
        nameOverride?: string | null;
        descriptionOverride?: string | null;
      }>,
    ) {
      if (state.flow.newToolForm) {
        state.flow.newToolForm = {
          ...state.flow.newToolForm,
          ...action.payload,
        };
      } else {
        state.flow.newToolForm = {
          kind: action.payload.kind ?? null,
          targetAgentId: action.payload.targetAgentId ?? null,
          targetAgentName: action.payload.targetAgentName ?? null,
          targetAgentHandle: action.payload.targetAgentHandle ?? null,
          modelHandle: action.payload.modelHandle ?? null,
          operation: action.payload.operation ?? null,
          nameOverride: action.payload.nameOverride ?? null,
          descriptionOverride: action.payload.descriptionOverride ?? null,
        };
      }
    },
    /** Flow canvas: clear new tool form */
    clearNewToolForm(state) {
      state.flow.newToolForm = null;
    },
    /** Flow canvas: initialize edit tool form from existing binding */
    initEditToolForm(
      state,
      action: PayloadAction<{
        bindingId: string;
        toolName: string;
        toolDisplayName: string;
        toolDescription: string;
        toolKind: string;
        nameOverride: string | null;
        descriptionOverride: string | null;
      }>,
    ) {
      state.flow.editToolForm = {
        bindingId: action.payload.bindingId,
        toolName: action.payload.toolName,
        toolDisplayName: action.payload.toolDisplayName,
        toolDescription: action.payload.toolDescription,
        toolKind: action.payload.toolKind,
        nameOverride: action.payload.nameOverride,
        descriptionOverride: action.payload.descriptionOverride,
      };
    },
    /** Flow canvas: update edit tool form field */
    updateEditToolForm(
      state,
      action: PayloadAction<{
        nameOverride?: string | null;
        descriptionOverride?: string | null;
      }>,
    ) {
      if (state.flow.editToolForm) {
        state.flow.editToolForm = {
          ...state.flow.editToolForm,
          ...action.payload,
        };
      }
    },
    /** Flow canvas: clear edit tool form */
    clearEditToolForm(state) {
      state.flow.editToolForm = null;
    },
    /** Marketplace: set search query */
    setMarketplaceSearchQuery(state, action: PayloadAction<string>) {
      state.marketplace.searchQuery = action.payload;
    },
    /** Marketplace: set active tab */
    setMarketplaceActiveTab(
      state,
      action: PayloadAction<"crm" | "integrations">,
    ) {
      state.marketplace.activeTab = action.payload;
    },
    /** Marketplace: select a tool for binding */
    selectToolForBinding(state, action: PayloadAction<SelectedTool>) {
      state.marketplace.selectedTool = action.payload;
      // Pre-fill the name and description overrides
      if (action.payload) {
        state.flow.newToolForm = {
          kind: action.payload.kind === "SYSTEM" ? "SYSTEM" : "MCP",
          targetAgentId: null,
          targetAgentName: null,
          targetAgentHandle: null,
          modelHandle: null,
          operation: null,
          nameOverride: action.payload.displayName,
          descriptionOverride: action.payload.description,
        };
      }
    },
    /** Marketplace: clear selected tool (go back to list) */
    clearSelectedTool(state) {
      state.marketplace.selectedTool = null;
      state.flow.newToolForm = null;
    },
    /** Marketplace: reset marketplace state */
    resetMarketplace(state) {
      state.marketplace.isLoading = false;
      state.marketplace.searchQuery = "";
      state.marketplace.selectedTool = null;
      state.marketplace.activeTab = "crm";
    },
    /** Draft: enter draft mode with a config */
    enterDraftMode(
      state,
      action: PayloadAction<{
        config: Record<string, unknown>;
        deploymentGroupId?: string;
      }>,
    ) {
      state.draft.isActive = true;
      state.draft.workingConfig = action.payload.config;
      state.draft.lastSavedConfig = action.payload.config;
      state.draft.deploymentGroupId = action.payload.deploymentGroupId || null;
      state.draft.isDirty = false;
    },
    /** Draft: update working config */
    updateDraftConfig(state, action: PayloadAction<Record<string, unknown>>) {
      state.draft.workingConfig = action.payload;
      state.draft.isDirty = true;
    },
    /** Draft: mark as saved (after successful save) */
    markDraftSaved(
      state,
      action: PayloadAction<{ deploymentGroupId: string }>,
    ) {
      state.draft.lastSavedConfig = state.draft.workingConfig;
      state.draft.deploymentGroupId = action.payload.deploymentGroupId;
      state.draft.isDirty = false;
    },
    /** Draft: clear draft state */
    clearDraft(state) {
      state.draft.isActive = false;
      state.draft.deploymentGroupId = null;
      state.draft.workingConfig = null;
      state.draft.lastSavedConfig = null;
      state.draft.isSaving = false;
      state.draft.isCommitting = false;
      state.draft.isDirty = false;
    },
  },
  extraReducers: (builder) => {
    // getAgents
    builder
      .addCase(getAgents.pending, (state) => {
        state.agents.isLoading = true;
      })
      .addCase(getAgents.fulfilled, (state, action) => {
        state.agents.isLoading = false;
        state.agents.data = action.payload;

        // Initialize Redux positions from persisted positions (only if not already set)
        for (const agent of action.payload) {
          const agentWithPosition = agent as unknown as {
            flowPositionX?: number | null;
            flowPositionY?: number | null;
          };
          if (
            agentWithPosition.flowPositionX != null &&
            agentWithPosition.flowPositionY != null &&
            !state.flow.positions[agent.id]
          ) {
            state.flow.positions[agent.id] = {
              x: agentWithPosition.flowPositionX,
              y: agentWithPosition.flowPositionY,
            };
          }
        }
      })
      .addCase(getAgents.rejected, (state) => {
        state.agents.isLoading = false;
      });

    // getAvailableTools
    builder
      .addCase(getAvailableTools.pending, (state) => {
        state.marketplace.isLoading = true;
      })
      .addCase(getAvailableTools.fulfilled, (state, action) => {
        state.marketplace.isLoading = false;
        state.marketplace.availableTools = action.payload;
      })
      .addCase(getAvailableTools.rejected, (state) => {
        state.marketplace.isLoading = false;
      });

    // getAgent
    builder
      .addCase(getAgent.pending, (state) => {
        state.currentAgent.isLoading = true;
      })
      .addCase(getAgent.fulfilled, (state, action) => {
        state.currentAgent.isLoading = false;
        state.currentAgent.data = action.payload;

        // Extract stages from version config if available
        const versionConfig = action.payload.versions?.[0]?.config as
          | Record<string, unknown>
          | undefined;
        const stages =
          (versionConfig?.stages as StageFormState[] | undefined) ?? [];
        const inputs =
          (versionConfig?.inputs as
            | Record<
                string,
                { type: string; required?: boolean; description?: string }
              >
            | undefined) ?? {};
        const onError =
          (versionConfig?.onError as "fail" | "skip" | "retry" | undefined) ??
          "fail";
        const respond =
          (versionConfig?.respond as RespondConfig | undefined) ?? null;
        const persona =
          (versionConfig?.persona as PersonaConfig | undefined) ?? null;

        // Populate editor state with both original and working copies
        const formState: AgentFormState = {
          id: action.payload.id,
          name: action.payload.name,
          handle: action.payload.handle ?? "",
          description: action.payload.description,
          system: action.payload.system,
          llmModelId: action.payload.llmModelId,
          persona,
          stages,
          inputs,
          onError,
          respond,
        };
        state.editor.original = formState;
        state.editor.working = { ...formState, stages: [...stages] };
      })
      .addCase(getAgent.rejected, (state) => {
        state.currentAgent.isLoading = false;
        state.currentAgent.data = null;
        state.editor.original = null;
        state.editor.working = null;
      });

    // createAgent
    builder
      .addCase(createAgent.pending, (state) => {
        state.editor.isSaving = true;
      })
      .addCase(createAgent.fulfilled, (state, action) => {
        state.editor.isSaving = false;
        // Add to agents list
        state.agents.data.unshift(action.payload as unknown as ListAgent);
      })
      .addCase(createAgent.rejected, (state) => {
        state.editor.isSaving = false;
      });

    // updateAgent
    builder
      .addCase(updateAgent.pending, (state) => {
        state.editor.isSaving = true;
      })
      .addCase(updateAgent.fulfilled, (state, action) => {
        state.editor.isSaving = false;
        state.currentAgent.data = action.payload;

        // Extract stages from version config if available
        const versionConfig = action.payload.versions?.[0]?.config as
          | Record<string, unknown>
          | undefined;
        const stages =
          (versionConfig?.stages as StageFormState[] | undefined) ?? [];
        const inputs =
          (versionConfig?.inputs as
            | Record<
                string,
                { type: string; required?: boolean; description?: string }
              >
            | undefined) ?? {};
        const onError =
          (versionConfig?.onError as "fail" | "skip" | "retry" | undefined) ??
          "fail";
        const persona =
          (versionConfig?.persona as PersonaConfig | undefined) ?? null;

        // Update both original and working state (they're now in sync after save)
        const formState: AgentFormState = {
          id: action.payload.id,
          name: action.payload.name,
          handle: action.payload.handle ?? "",
          description: action.payload.description,
          system: action.payload.system,
          llmModelId: action.payload.llmModelId,
          persona,
          stages,
          inputs,
          onError,
        };
        state.editor.original = formState;
        state.editor.working = { ...formState, stages: [...stages] };

        // Update in agents list
        const index = state.agents.data.findIndex(
          (a) => a.id === action.payload.id,
        );
        if (index >= 0) {
          state.agents.data[index] = action.payload as unknown as ListAgent;
        }
      })
      .addCase(updateAgent.rejected, (state) => {
        state.editor.isSaving = false;
      });

    // deleteAgent
    builder
      .addCase(deleteAgent.pending, (state) => {
        state.editor.isDeleting = true;
      })
      .addCase(deleteAgent.fulfilled, (state, action) => {
        state.editor.isDeleting = false;
        // Remove from agents list
        state.agents.data = state.agents.data.filter(
          (a) => a.id !== action.payload,
        );
        // Clear current agent
        state.currentAgent.data = null;
        state.editor.original = null;
        state.editor.working = null;
      })
      .addCase(deleteAgent.rejected, (state) => {
        state.editor.isDeleting = false;
      });

    // optimizeAgent
    builder
      .addCase(optimizeAgent.pending, (state) => {
        state.editor.isOptimizing = true;
      })
      .addCase(optimizeAgent.fulfilled, (state, action) => {
        state.editor.isOptimizing = false;
        // Update working state with optimized values
        if (state.editor.working) {
          state.editor.working.description = action.payload.description;
          state.editor.working.system = action.payload.system;
        }
      })
      .addCase(optimizeAgent.rejected, (state) => {
        state.editor.isOptimizing = false;
      });

    // deepOptimizeAgent (from detail page)
    builder
      .addCase(deepOptimizeAgent.pending, (state) => {
        state.editor.isOptimizing = true;
      })
      .addCase(deepOptimizeAgent.fulfilled, (state, action) => {
        state.editor.isOptimizing = false;
        state.lastOptimizationStats = action.payload.stats;
        // Update working state with optimized values
        if (state.editor.working) {
          state.editor.working.description = action.payload.description;
          state.editor.working.system = action.payload.system;
        }
      })
      .addCase(deepOptimizeAgent.rejected, (state) => {
        state.editor.isOptimizing = false;
      });

    // deepOptimizeAgentById (from list page)
    builder
      .addCase(deepOptimizeAgentById.pending, (state, action) => {
        state.optimizingAgentId = action.meta.arg.id;
      })
      .addCase(deepOptimizeAgentById.fulfilled, (state, action) => {
        state.optimizingAgentId = null;
        state.lastOptimizationStats = action.payload.stats;
        // Update in agents list
        const index = state.agents.data.findIndex(
          (a) => a.id === action.payload.agent.id,
        );
        if (index >= 0) {
          state.agents.data[index] = action.payload
            .agent as unknown as ListAgent;
        }
      })
      .addCase(deepOptimizeAgentById.rejected, (state) => {
        state.optimizingAgentId = null;
      });

    // saveDraft
    builder
      .addCase(saveDraft.pending, (state) => {
        state.draft.isSaving = true;
      })
      .addCase(saveDraft.fulfilled, (state, action) => {
        state.draft.isSaving = false;
        state.draft.deploymentGroupId = action.payload.deploymentGroupId;
        state.draft.lastSavedConfig = state.draft.workingConfig;
        state.draft.isDirty = false;
      })
      .addCase(saveDraft.rejected, (state) => {
        state.draft.isSaving = false;
      });

    // commitDraft
    builder
      .addCase(commitDraft.pending, (state) => {
        state.draft.isCommitting = true;
      })
      .addCase(commitDraft.fulfilled, (state) => {
        state.draft.isCommitting = false;
        // Clear draft state after successful commit
        state.draft.isActive = false;
        state.draft.deploymentGroupId = null;
        state.draft.workingConfig = null;
        state.draft.lastSavedConfig = null;
        state.draft.isDirty = false;
      })
      .addCase(commitDraft.rejected, (state) => {
        state.draft.isCommitting = false;
      });

    // discardDraft
    builder
      .addCase(discardDraft.pending, (state) => {
        state.draft.isSaving = true;
      })
      .addCase(discardDraft.fulfilled, (state) => {
        state.draft.isSaving = false;
        // Clear draft state after successful discard
        state.draft.isActive = false;
        state.draft.deploymentGroupId = null;
        state.draft.workingConfig = null;
        state.draft.lastSavedConfig = null;
        state.draft.isDirty = false;
      })
      .addCase(discardDraft.rejected, (state) => {
        state.draft.isSaving = false;
      });

    // loadAgentVersions
    builder
      .addCase(loadAgentVersions.pending, (state) => {
        state.editor.versionsLoading = true;
      })
      .addCase(loadAgentVersions.fulfilled, (state, action) => {
        state.editor.versionsLoading = false;
        state.editor.versions = action.payload;
        // Auto-select the first published version, or first version if none published
        if (!state.editor.selectedVersionId && action.payload.length > 0) {
          const publishedVersion = action.payload.find((v) => v.isPublished);
          const firstVersion = action.payload[0];
          const selectedVersion = publishedVersion || firstVersion;
          if (selectedVersion) {
            state.editor.selectedVersionId = selectedVersion.id;
            state.editor.selectedVersionIsPublished =
              selectedVersion.isPublished;
          }
        }
      })
      .addCase(loadAgentVersions.rejected, (state) => {
        state.editor.versionsLoading = false;
      });

    // loadAgentVersion
    builder
      .addCase(loadAgentVersion.pending, (state) => {
        state.currentAgent.isLoading = true;
      })
      .addCase(loadAgentVersion.fulfilled, (state, action) => {
        state.currentAgent.isLoading = false;
        state.editor.selectedVersionId = action.payload.id;
        state.editor.selectedVersionIsPublished = action.payload.isPublished;

        // Extract data from version config
        const config = action.payload.config || {};
        const stages = (config.stages as StageFormState[] | undefined) ?? [];
        const inputs =
          (config.inputs as
            | Record<
                string,
                { type: string; required?: boolean; description?: string }
              >
            | undefined) ?? {};
        const onError =
          (config.onError as "fail" | "skip" | "retry" | undefined) ?? "fail";
        const respond = (config.respond as RespondConfig | undefined) ?? null;
        const persona = (config.persona as PersonaConfig | undefined) ?? null;

        // Use agent data from the version response, falling back to config
        const agent = action.payload.agent;
        const formState: AgentFormState = {
          id: agent.id,
          name: (config.name as string) || agent.name,
          handle: (config.handle as string) || agent.handle || "",
          description: (config.description as string) || agent.description,
          system: (config.system as string) || agent.system,
          llmModelId: (config.llmModelId as string) || agent.llmModelId,
          persona,
          stages,
          inputs,
          onError,
          respond,
        };
        state.editor.original = formState;
        state.editor.working = { ...formState, stages: [...stages] };
      })
      .addCase(loadAgentVersion.rejected, (state) => {
        state.currentAgent.isLoading = false;
      });

    // updateDraft
    builder
      .addCase(updateDraft.pending, (state) => {
        state.editor.isSaving = true;
      })
      .addCase(updateDraft.fulfilled, (state, action) => {
        state.editor.isSaving = false;
        // Update the version in the versions list
        const versionIndex = state.editor.versions.findIndex(
          (v) => v.id === action.payload.versionId,
        );
        if (versionIndex >= 0) {
          state.editor.versions[versionIndex] = {
            ...state.editor.versions[versionIndex]!,
            version: action.payload.version,
            versionLabel: action.payload.versionLabel,
          };
        }
        // Sync original with working after save
        if (state.editor.working) {
          state.editor.original = { ...state.editor.working };
        }
      })
      .addCase(updateDraft.rejected, (state) => {
        state.editor.isSaving = false;
      });

    // publishVersion
    builder
      .addCase(publishVersion.pending, (state) => {
        state.editor.isPublishing = true;
      })
      .addCase(publishVersion.fulfilled, (state, action) => {
        state.editor.isPublishing = false;
        state.editor.selectedVersionIsPublished = true;
        // Update the version in the versions list
        const versionIndex = state.editor.versions.findIndex(
          (v) => v.id === action.payload.versionId,
        );
        if (versionIndex >= 0) {
          state.editor.versions[versionIndex] = {
            ...state.editor.versions[versionIndex]!,
            isPublished: true,
            trafficWeight: action.payload.trafficWeight,
          };
        }
        // Set other versions' traffic weight to 0
        for (let i = 0; i < state.editor.versions.length; i++) {
          if (i !== versionIndex) {
            state.editor.versions[i] = {
              ...state.editor.versions[i]!,
              trafficWeight: 0,
            };
          }
        }
      })
      .addCase(publishVersion.rejected, (state) => {
        state.editor.isPublishing = false;
      });

    // deleteDraftVersion
    builder
      .addCase(deleteDraftVersion.pending, (state) => {
        state.editor.isDeleting = true;
      })
      .addCase(deleteDraftVersion.fulfilled, (state, action) => {
        state.editor.isDeleting = false;
        // Remove the version from the list
        state.editor.versions = state.editor.versions.filter(
          (v) => v.id !== action.payload.versionId,
        );
        // If the deleted version was selected, select the first published version
        if (state.editor.selectedVersionId === action.payload.versionId) {
          const publishedVersion = state.editor.versions.find(
            (v) => v.isPublished,
          );
          const firstVersion = state.editor.versions[0];
          const newSelected = publishedVersion || firstVersion;
          state.editor.selectedVersionId = newSelected?.id || null;
          state.editor.selectedVersionIsPublished =
            newSelected?.isPublished ?? true;
        }
      })
      .addCase(deleteDraftVersion.rejected, (state) => {
        state.editor.isDeleting = false;
      });
  },
});

// Selectors
export const selectAgents = (state: RootState) => state.agents.agents;
export const selectCurrentAgent = (state: RootState) =>
  state.agents.currentAgent;
export const selectAgentEditor = (state: RootState) => state.agents.editor;
export const selectOptimizingAgentId = (state: RootState) =>
  state.agents.optimizingAgentId;
export const selectLastOptimizationStats = (state: RootState) =>
  state.agents.lastOptimizationStats;

/** Select the original (saved) agent data */
export const selectAgentOriginal = (state: RootState) =>
  state.agents.editor.original;

/** Select the working (edited) agent data */
export const selectAgentWorking = (state: RootState) =>
  state.agents.editor.working;

/** Check if there are unsaved changes (working differs from original) */
export const selectHasAgentChanges = (state: RootState): boolean => {
  const { original, working } = state.agents.editor;
  if (!original || !working) return false;

  return (
    original.name !== working.name ||
    original.description !== working.description ||
    original.system !== working.system ||
    original.llmModelId !== working.llmModelId
  );
};

/** Select tool bindings for the current agent (placeholder - returns empty array until feature is implemented) */
export const selectToolBindings = (_state: RootState): ToolBindingRow[] => {
  return [];
};

/** Version selectors */
export const selectEditorVersions = (state: RootState) =>
  state.agents.editor.versions;
export const selectSelectedVersionId = (state: RootState) =>
  state.agents.editor.selectedVersionId;
export const selectSelectedVersionIsPublished = (state: RootState) =>
  state.agents.editor.selectedVersionIsPublished;
export const selectVersionsLoading = (state: RootState) =>
  state.agents.editor.versionsLoading;
export const selectIsPublishing = (state: RootState) =>
  state.agents.editor.isPublishing;
export const selectSelectedVersion = (state: RootState) => {
  const { versions, selectedVersionId } = state.agents.editor;
  return versions.find((v) => v.id === selectedVersionId) || null;
};

/** Flow canvas selectors */
export const selectFlowState = (state: RootState) => state.agents.flow;
export const selectScopedAgentId = (state: RootState) =>
  state.agents.flow.scopedAgentId;
export const selectScopedToolId = (state: RootState) =>
  state.agents.flow.scopedToolId;
export const selectAgentPositions = (state: RootState) =>
  state.agents.flow.positions;
export const selectScopedAgent = (state: RootState) => {
  const scopedId = state.agents.flow.scopedAgentId;
  if (!scopedId) return null;
  return state.agents.agents.data.find((a) => a.id === scopedId) || null;
};
export const selectNewToolForm = (state: RootState) =>
  state.agents.flow.newToolForm;
export const selectEditToolForm = (state: RootState) =>
  state.agents.flow.editToolForm;

/** Marketplace selectors */
export const selectMarketplace = (state: RootState) => state.agents.marketplace;
export const selectAvailableTools = (state: RootState) =>
  state.agents.marketplace.availableTools;
export const selectMarketplaceSearchQuery = (state: RootState) =>
  state.agents.marketplace.searchQuery;
export const selectMarketplaceSelectedTool = (state: RootState) =>
  state.agents.marketplace.selectedTool;
export const selectMarketplaceActiveTab = (state: RootState) =>
  state.agents.marketplace.activeTab;
export const selectMarketplaceIsLoading = (state: RootState) =>
  state.agents.marketplace.isLoading;

/** Draft selectors */
export const selectDraft = (state: RootState) => state.agents.draft;
export const selectDraftIsActive = (state: RootState) =>
  state.agents.draft.isActive;
export const selectDraftDeploymentGroupId = (state: RootState) =>
  state.agents.draft.deploymentGroupId;
export const selectDraftWorkingConfig = (state: RootState) =>
  state.agents.draft.workingConfig;
export const selectDraftIsDirty = (state: RootState) =>
  state.agents.draft.isDirty;
export const selectDraftIsSaving = (state: RootState) =>
  state.agents.draft.isSaving;
export const selectDraftIsCommitting = (state: RootState) =>
  state.agents.draft.isCommitting;

/** Stage selectors */
export const selectStages = (state: RootState): StageFormState[] =>
  state.agents.editor.working?.stages ?? [];
export const selectAgentInputsSchema = (state: RootState) =>
  state.agents.editor.working?.inputs ?? {};

// Action creators
export const {
  updateEditorField,
  addStage,
  updateStage,
  removeStage,
  reorderStages,
  updateAgentInputs,
  resetEditor,
  setSelectedVersionId,
  initEditorFromAgent,
  resetCurrentAgent,
  resetWorkingToOriginal,
  scopeFlowAgent,
  scopeFlowTool,
  updateAgentPosition,
  updateAgentPositions,
  clearFlowScope,
  initNewAgentToolForm,
  updateNewToolForm,
  clearNewToolForm,
  initEditToolForm,
  updateEditToolForm,
  clearEditToolForm,
  setMarketplaceSearchQuery,
  setMarketplaceActiveTab,
  selectToolForBinding,
  clearSelectedTool,
  resetMarketplace,
  enterDraftMode,
  updateDraftConfig,
  markDraftSaved,
  clearDraft,
} = agentSlice.actions;

export default agentSlice.reducer;
